import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { WidgetExtended } from '@/types/dashboard'
import useWidgetData from '@/composables/useWidgetData'
import { editableWidgetId } from '@/composables/useWidgetEditModal'
import { isAiSummaryWidget } from '@/helpers/widgetUtils'

/* -------------------------------------
 * Constants
 * ----------------------------------- */
const MAX_DELAY = 2 ** 31 - 1
const MIN_DELAY = 1000
const INITIAL_POLLING_INTERVAL = 6000 // Start with 6 seconds
const POLLING_MULTIPLIER = 1.5 // Increase by 1.5x each attempt
const MAX_POLLING_INTERVAL = 60000 // Cap at 60 seconds
const MAX_POLLING_ATTEMPTS = 10

type WithNextRunTime = {
    NextRunTime?: string | number
}

/* -------------------------------------
 * Composable
 * ----------------------------------- */
export default function <
    T extends WithNextRunTime,
    P extends Record<K, WidgetExtended>,
    K extends keyof P
> (props: Readonly<P>, key: K) {

    const {
        isLoading,
        data,
        dataLoaded,
        widgetRef,
        fetchData,
    } = useWidgetData<T, P, K>(props, key)

    const timeoutId = ref<number | null>(null)
    const pollingAttempts = ref(0)
    const isFetching = ref(false)
    const currentPollingInterval = ref(INITIAL_POLLING_INTERVAL)
    let isDestroyed = false

    /* -------------------------------------
     * Computed
     * ----------------------------------- */
    const isWidgetInEdit = computed(
        () => editableWidgetId.value === widgetRef.value.WidgetID
    )

    /* -------------------------------------
     * Utils
     * ----------------------------------- */
    function clearTimer () {
        if (timeoutId.value !== null) {
            clearTimeout(timeoutId.value)
            timeoutId.value = null
        }
    }

    function toTimestamp (value?: string | number): number {
        if (!value) return 0
        if (typeof value === 'number') return value

        const ts = new Date(value).getTime()
        return Number.isNaN(ts) ? 0 : ts
    }

    function computeDelay (value?: string | number): number | null {
        const ts = toTimestamp(value)
        if (!ts) return null

        const delay = ts - Date.now()
        if (delay < MIN_DELAY) return null

        return Math.min(delay, MAX_DELAY)
    }

    /* -------------------------------------
     * Scheduler primitives
     * ----------------------------------- */
    function scheduleNext (delay: number) {
        clearTimer()
        timeoutId.value = window.setTimeout(run, delay)
    }

    function getNextPollingInterval (): number {
        const nextInterval = currentPollingInterval.value * POLLING_MULTIPLIER
        return Math.min(nextInterval, MAX_POLLING_INTERVAL)
    }

    function resetPollingInterval () {
        currentPollingInterval.value = INITIAL_POLLING_INTERVAL
        pollingAttempts.value = 0
    }

    function pollFallback () {
        if (++pollingAttempts.value >= MAX_POLLING_ATTEMPTS) {
            clearTimer()
            return
        }

        // Use current interval and increase it for next time
        scheduleNext(currentPollingInterval.value)

        // Calculate next interval with exponential backoff
        currentPollingInterval.value = getNextPollingInterval()
    }

    /* -------------------------------------
     * Core runner
     * ----------------------------------- */
    async function run (forceFetch = false): Promise<void> {
        if (isDestroyed) return
        if (isFetching.value) return
        if (isWidgetInEdit.value) return clearTimer()

        const delay = computeDelay(data.value?.NextRunTime)

        // If forceFetch is true, skip the delay check and fetch immediately
        if (!forceFetch && delay !== null) {
            // Future NextRunTime → schedule only
            resetPollingInterval() // Reset interval when we have a valid NextRunTime
            scheduleNext(delay)
            return
        }

        // No / past NextRunTime OR forceFetch → fetch immediately
        isFetching.value = true
        try {
            await fetchData()

            if (isDestroyed) return

            const nextDelay = computeDelay(data.value?.NextRunTime)
            if (nextDelay !== null) {
                resetPollingInterval()
                scheduleNext(nextDelay)
            } else {
                pollFallback() // Use exponential backoff when no valid NextRunTime
            }
        } finally {
            isFetching.value = false
        }
    }

    /* -------------------------------------
     * Start lifecycle
     * ----------------------------------- */
    function start () {
        run()
    }

    // Watch for widget configuration changes (for AI summary widgets)
    // When widget props change after update, trigger immediate refetch
    if (isAiSummaryWidget(widgetRef.value)) {
        // Track the last WidgetConfig to detect actual changes
        let lastWidgetConfigString = JSON.stringify(widgetRef.value?.WidgetConfig || [])

        watch(
            () => props[key],
            (newWidget, oldWidget) => {
                // Only trigger if it's the same widget (not a complete replacement)
                if (!newWidget?.WidgetID || !oldWidget?.WidgetID) return
                if (newWidget.WidgetID !== oldWidget.WidgetID) return

                // Check if WidgetConfig actually changed
                const newConfigString = JSON.stringify(newWidget?.WidgetConfig || [])
                const configChanged = newConfigString !== lastWidgetConfigString

                if (configChanged && !isFetching.value && !isWidgetInEdit.value) {
                    // Widget configuration was updated - fetch new data immediately
                    lastWidgetConfigString = newConfigString
                    resetPollingInterval() // Reset interval on manual update
                    run(true) // Force immediate fetch, bypassing NextRunTime check
                }
            },
            { deep: false } // Use shallow watch to avoid unnecessary triggers
        )
    }

    start()
    onBeforeUnmount(() => {
        isDestroyed = true
        clearTimer()
    })

    return {
        isLoading,
        dataLoaded,
        data,
        widgetRef,
        run,
        start,
    }
}
